import { useState, MouseEvent } from "react";
import { ALERT_TABLE_COLUMNS } from "./AlertColumns";
import { PaginatedTable } from "components/Table/Table";
import { last, slice } from "lodash";

import { Alert } from "interfaces/alert";
import { Row } from "react-table";

import { Paper } from "@mui/material";
import { styled } from "@mui/material/styles";

export interface AlertsTableProps {
  alertsTableData: Alert[];
  loading: boolean;
  pageIndex: number;
  pageSize: number;
  count: number;
  selectedAlerts: Alert[];
  selectedOnCurrentPage: any[];
  onSelectAlerts: (newSelectedAlerts: any, activeAlert: any) => void;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onUpdatePageSize: (pageSize: number) => void;
  onClickDismissAlerts: (alertToDismiss: Alert[]) => void;
  onSortingChange: () => void;
}

export const AlertsTable = ({
  alertsTableData,
  loading,
  pageIndex,
  pageSize,
  count,
  selectedOnCurrentPage,
  selectedAlerts,
  onSelectAlerts,
  onPageChange,
  onUpdatePageSize,
  onClickDismissAlerts,
  onSortingChange
}: AlertsTableProps): JSX.Element => {
  const [selectedAlertIndex, setSelectedAlertIndex] = useState<
    number | undefined
  >(0);

  const getIsAlertSelected = (alertId: number): boolean => {
    return selectedOnCurrentPage?.some(({ alert_id }) => alert_id === alertId);
  };

  const onClickRow = (
    { index, original: alert }: Row<Alert>,
    event: MouseEvent<HTMLElement>
  ): void => {
    // Press ( Ctrl + Click ) select and deselect any row.
    if (event.ctrlKey) {
      handleOnPressCtrlClickRow(alert, index);
    } else if (event.shiftKey) {
      // Press ( Shift + Click ) select and deselect any range of rows.
      handleOnPressShiftClickRow(alert, index);
    } else {
      // Click row to select this row.
      setSelectedAlertIndex(index);
      onSelectAlerts([alert], alert);
    }
  };

  const handleOnPressCtrlClickRow = (newAlert: Alert, index: number): void => {
    // find if already selected
    let isSelected = selectedAlerts.some(
      ({ alert_id }) => alert_id === newAlert.alert_id
    );
    // remove if already selected
    let updatedSelectedRows = !isSelected
      ? [...selectedAlerts, newAlert]
      : selectedAlerts.filter(({ alert_id }) => alert_id !== newAlert.alert_id);

    setSelectedAlertIndex(index);
    onSelectAlerts(updatedSelectedRows, last(updatedSelectedRows));
  };

  const handleOnPressShiftClickRow = (newAlert: Alert, index: number): void => {
    if (selectedAlertIndex !== undefined) {
      if (selectedAlertIndex < index) {
        handleSelectRows({ start: selectedAlertIndex, end: index, newAlert });
      } else {
        handleSelectRows({ start: index, end: selectedAlertIndex, newAlert });
      }
    }
  };

  interface HandleSelectRowsArgs {
    start: number;
    end: number;
    newAlert: Alert;
  }

  const handleSelectRows = ({
    start,
    end,
    newAlert
  }: HandleSelectRowsArgs): void => {
    let alertsSlice = slice(alertsTableData, start, end + 1);
    setSelectedAlertIndex(start);
    onSelectAlerts(alertsSlice, newAlert);
  };

  // When the page is changed, clear the last selected index
  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ): void => {
    setSelectedAlertIndex(undefined);
    onPageChange(event, page);
  };

  const handleSelectAll = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const { target } = event;

    if (target.checked) {
      onSelectAlerts(
        [...selectedAlerts, ...alertsTableData],
        last(alertsTableData)
      );
    } else {
      onSelectAlerts([], undefined);
    }
  };

  const toggleSelectRows = (
    newAlert: Alert,
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    e.stopPropagation();
    // Need to case the event to be a KeyboardEvent
    // since the ChangeEvent<HTMLInputElement> does not have a shiftKey property
    const keyboardEvent = e.nativeEvent as KeyboardEvent;

    if (keyboardEvent.shiftKey) {
      // Press ( Shift + Click ) select and deselect any range of rows.
      handleOnPressShiftClickRow(newAlert, index);
    } else {
      if (e.target.checked) {
        onSelectAlerts([...selectedAlerts, newAlert], newAlert);
      } else {
        let updatedSelectedRows = selectedAlerts.filter(
          ({ alert_id }) => alert_id !== newAlert.alert_id
        );
        onSelectAlerts(updatedSelectedRows, last(updatedSelectedRows));
      }
      setSelectedAlertIndex(index);
    }
  };

  const columns = ALERT_TABLE_COLUMNS({
    alertsTableData,
    selectedOnCurrentPage,
    getIsAlertSelected,
    toggleSelectRows,
    handleSelectAll,
    onClickDismissAlerts
  });

  return (
    <SiteTableScroll elevation={0}>
      <PaginatedTable
        data={alertsTableData}
        columns={columns}
        loading={loading}
        dataQa="alerts-table"
        count={count}
        page={pageIndex}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onSortingChange={onSortingChange}
        onPageSizeChange={(
          event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
        ) => onUpdatePageSize(parseInt(event.target.value))}
        onClickRow={onClickRow}
        isRowSelected={alert => selectedAlerts.includes(alert)}
      />
    </SiteTableScroll>
  );
};

const SiteTableScroll = styled(Paper)`
  display: flex;
  flex-flow: column nowrap;
  overflow-y: auto;
  flex-grow: 1;
  border: 1px solid rgba(224, 224, 224, 1);
  border-radius: 4px;
`;
